import { useState, useEffect } from 'react';
import { 
  getAllCases, 
  createCase, 
  updateCase, 
  deleteCase, 
  Case 
} from '@/services/caseService';
import { uploadImage } from '@/services/imageService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { 
  Plus, 
  Edit, 
  Trash2, 
  RefreshCw, 
  Image as ImageIcon,
  Upload
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { db } from '@/lib/firebase';
import { collection, addDoc, query, orderBy, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Eye, FileText, Calendar } from 'lucide-react';

const Cases = () => {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<Case | null>(null);
  const [uploadingBeforeImage, setUploadingBeforeImage] = useState(false);
  const [uploadingAfterImage, setUploadingAfterImage] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    beforeImage: '',
    afterImage: '',
    category: ''
  });

  const categories = [
    "زراعة الأسنان",
    "تقويم الأسنان",
    "تجميل الأسنان",
    "علاج اللثة",
    "حشوات تجميلية",
    "تبييض الأسنان"
  ];

  const fetchCases = async () => {
    try {
      const data = await getAllCases();
      setCases(data);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast({
        variant: 'destructive',
        title: 'خطأ في تحميل الحالات',
        description: 'حدث خطأ أثناء تحميل الحالات',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'before' | 'after') => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (type === 'before') {
        setUploadingBeforeImage(true);
      } else {
        setUploadingAfterImage(true);
      }

      const imageUrl = await uploadImage(file);
      setFormData(prev => ({ 
        ...prev, 
        [type === 'before' ? 'beforeImage' : 'afterImage']: imageUrl 
      }));

      toast({
        title: 'تم رفع الصورة',
        description: 'تم رفع الصورة بنجاح',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        variant: 'destructive',
        title: 'خطأ في رفع الصورة',
        description: 'حدث خطأ أثناء رفع الصورة',
      });
    } finally {
      if (type === 'before') {
        setUploadingBeforeImage(false);
      } else {
        setUploadingAfterImage(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const caseData = {
        ...formData,
        createdAt: new Date().toISOString()
      };

      if (editingCase) {
        await updateCase(editingCase.id!, caseData);
        toast({
          title: 'تم تحديث الحالة',
          description: 'تم تحديث الحالة بنجاح',
        });
      } else {
        await createCase(caseData);
        toast({
          title: 'تم إضافة الحالة',
          description: 'تم إضافة الحالة بنجاح',
        });
      }

      setIsDialogOpen(false);
      setEditingCase(null);
      setFormData({
        beforeImage: '',
        afterImage: '',
        category: ''
      });
      fetchCases();
    } catch (error) {
      console.error('Error saving case:', error);
      toast({
        variant: 'destructive',
        title: 'خطأ في حفظ الحالة',
        description: 'حدث خطأ أثناء حفظ الحالة',
      });
    }
  };

  const handleEdit = (caseItem: Case) => {
    setEditingCase(caseItem);
    setFormData({
      beforeImage: caseItem.beforeImage,
      afterImage: caseItem.afterImage,
      category: caseItem.category
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (caseId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الحالة؟')) return;

    try {
      await deleteCase(caseId);
      await fetchCases();
      toast({
        title: 'تم حذف الحالة',
        description: 'تم حذف الحالة بنجاح',
      });
    } catch (error) {
      console.error('Error deleting case:', error);
      toast({
        variant: 'destructive',
        title: 'خطأ في حذف الحالة',
        description: 'حدث خطأ أثناء حذف الحالة',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl font-bold">إدارة الحالات</CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary hover:bg-primary/90"
                onClick={() => {
                  setEditingCase(null);
                  setFormData({ beforeImage: '', afterImage: '', category: '' });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                إضافة حالة جديدة
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCase ? 'تعديل حالة' : 'إضافة حالة جديدة'}</DialogTitle>
              </DialogHeader>
              <DialogDescription>
                يرجى تعبئة جميع الحقول المطلوبة لإضافة أو تعديل الحالة.
              </DialogDescription>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="category">الفئة</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الفئة" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="beforeImage">صورة قبل العلاج</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="beforeImage"
                      type="file"
                      onChange={(e) => handleImageUpload(e, 'before')}
                      disabled={uploadingBeforeImage}
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('beforeImage')?.click()}
                      disabled={uploadingBeforeImage}
                      className="shrink-0"
                    >
                      {uploadingBeforeImage ? 'جاري الرفع...' : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                  {formData.beforeImage && (
                    <div className="mt-2 relative w-32 h-32 rounded-md overflow-hidden">
                      <img src={formData.beforeImage} alt="قبل" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="afterImage">صورة بعد العلاج</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="afterImage"
                      type="file"
                      onChange={(e) => handleImageUpload(e, 'after')}
                      disabled={uploadingAfterImage}
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('afterImage')?.click()}
                      disabled={uploadingAfterImage}
                      className="shrink-0"
                    >
                      {uploadingAfterImage ? 'جاري الرفع...' : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                  {formData.afterImage && (
                    <div className="mt-2 relative w-32 h-32 rounded-md overflow-hidden">
                      <img src={formData.afterImage} alt="بعد" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                  {editingCase ? 'تحديث' : 'إضافة'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>صورة قبل</TableHead>
                <TableHead>صورة بعد</TableHead>
                <TableHead>الفئة</TableHead>
                <TableHead className="text-right">الإجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cases.map((caseItem) => (
                <TableRow key={caseItem.id}>
                  <TableCell>
                    {caseItem.beforeImage && (
                      <img src={caseItem.beforeImage} alt="قبل" className="w-16 h-16 object-cover rounded-md" />
                    )}
                  </TableCell>
                  <TableCell>
                    {caseItem.afterImage && (
                      <img src={caseItem.afterImage} alt="بعد" className="w-16 h-16 object-cover rounded-md" />
                    )}
                  </TableCell>
                  <TableCell>{caseItem.category}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(caseItem)}
                      className="mr-2"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(caseItem.id!)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Cases; 